/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { cloneDeep } from 'lodash'

import type {
  ClusterInstanceDiskInfo,
  ClusterInstanceMemoryInfo,
} from '@modules/cloud-api/v1/types'
import type { StackDeployment } from '@modules/ui-types'

import { getResourceByRefId } from '@/lib/stackDeployments/selectors/fundamentals'

import type { SetInstanceCapacityAction } from '@/actions/stackDeployments/types'

export default function setStackDeploymentInstanceCapacity(
  deployment: StackDeployment,
  action: SetInstanceCapacityAction,
) {
  const nextDeployment = cloneDeep(deployment)
  const { resourceKind, refId, instanceIds, instanceCapacity } = action.meta

  const resource = getResourceByRefId({
    deployment: nextDeployment,
    resourceType: resourceKind,
    refId,
  })
  const instances = resource?.info.topology.instances ?? []

  // We expect to find the instance by its ID
  const resourceInstances = instanceIds.map((id) => {
    const foundInstance = instances.find((each) => each.instance_name === id)

    if (foundInstance == null) {
      throw new Error(`Could not find instance with id [${id}]`)
    }

    return foundInstance
  })

  resourceInstances.forEach((instance) => {
    const currentMemoryCapacity = instance.memory?.instance_capacity_planned ?? 0
    const currentStorageCapacity = instance.disk?.disk_space_available ?? 0
    const storageCapacity =
      currentMemoryCapacity === 0
        ? 0
        : (instanceCapacity * currentStorageCapacity) / currentMemoryCapacity

    const instanceMemory = instance.memory ?? (instance.memory = {} as ClusterInstanceMemoryInfo)
    const instanceDisk = instance.disk ?? (instance.disk = {} as ClusterInstanceDiskInfo)

    instanceMemory.instance_capacity = instanceCapacity
    instanceDisk.disk_space_available = storageCapacity
  })

  return nextDeployment
}
